import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DocumentTextIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  TagIcon,
  FolderIcon,
  PlayIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { catalogoService, type CatalogoHistorico } from '../api/catalogo';
import { pesquisasService } from '../api/pesquisas';
import { useSignalR } from '../hooks/useSignalR';
import { useToast } from '../components/ui/Toast';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import { ClockIcon, TrashIcon } from '@heroicons/react/24/outline';
import type {
  PesquisaAlmox,
  PesquisaEmpresa,
  PesquisaGrupo,
  PesquisaMarca,
} from '../types/pesquisas';
import type { FiltrosCatalogo } from '../types/catalogo';
import { OPCOES_AGRUPAMENTO, OPCOES_ORDENACAO } from '../types/catalogo';
import { logError } from '../utils/clientLog';
import { FiltrosProduto } from '../components/ui/FiltrosProduto';
import { btnPrimario, inputIris } from '../components/ui/iris';

// ── Dropdown multiselect inline ──────────────────────────────────────────────

interface DropdownMultiProps<T> {
  label: string;
  icon: React.ReactNode;
  items: T[];
  selected: T[];
  onToggle: (item: T) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getSubLabel?: (item: T) => string;
  loading?: boolean;
  placeholder?: string;
  required?: boolean;
}

function DropdownMulti<T>({
  label, icon, items, selected, onToggle, getId, getLabel, getSubLabel,
  loading, placeholder = 'Buscar...', required,
}: DropdownMultiProps<T>) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedIds = new Set(selected.map(getId));
  const filtrados = items.filter(i => {
    const text = `${getLabel(i)} ${getSubLabel?.(i) ?? ''}`.toLowerCase();
    return text.includes(busca.toLowerCase());
  });

  const qtd = selected.length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAberto(v => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-iris border text-left text-sm transition-colors
          ${aberto
            ? 'border-accent ring-[3px] ring-accent-soft bg-surface'
            : 'border-line bg-surface-2 hover:bg-surface'
          }`}
      >
        <span className="text-tx-3 flex-shrink-0">{icon}</span>
        <span className="flex-1 min-w-0 truncate text-tx font-medium">
          {label}
        </span>
        {qtd > 0 && (
          <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-accent-soft text-accent-text">
            {qtd}
          </span>
        )}
        {required && qtd === 0 && (
          <span className="flex-shrink-0 text-[10px] text-danger font-medium">obrig.</span>
        )}
        <ChevronDownIcon className={`w-4 h-4 text-tx-3 flex-shrink-0 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {/* Chips */}
      {qtd > 0 && !aberto && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selected.slice(0, 3).map(item => (
            <span key={getId(item)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-accent-soft text-accent-text border border-line">
              {getLabel(item).slice(0, 20)}{getLabel(item).length > 20 ? '...' : ''}
              <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(item); }} className="hover:text-danger">
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
          {qtd > 3 && <span className="text-[11px] text-tx-3 self-center">+{qtd - 3}</span>}
        </div>
      )}

      {/* Dropdown */}
      {aberto && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-line rounded-iris-lg shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-line">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tx-3" />
              <input
                type="text"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder={placeholder}
                className={`w-full pl-8 ${inputIris}`}
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-xs text-tx-3"><ArrowPathIcon className="w-4 h-4 animate-spin mx-auto mb-1" />Carregando...</div>
            ) : filtrados.length === 0 ? (
              <div className="p-4 text-center text-xs text-tx-3">Nenhum resultado</div>
            ) : (
              filtrados.map(item => {
                const checked = selectedIds.has(getId(item));
                return (
                  <label
                    key={getId(item)}
                    className={`flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer transition-colors
                      ${checked ? 'bg-accent-soft' : 'hover:bg-surface-2'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(item)}
                      className="w-3.5 h-3.5 text-accent rounded border-line-strong focus:ring-accent"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-tx">{getLabel(item)}</div>
                      {getSubLabel && (
                        <div className="truncate text-[10px] text-tx-3">{getSubLabel(item)}</div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────

export const GerarCatalogoPage: React.FC = () => {
  // Aba ativa
  const [aba, setAba] = useState<'gerar' | 'historico'>('gerar');

  // Dados carregados
  const [empresas, setEmpresas] = useState<PesquisaEmpresa[]>([]);
  const [almoxarifados, setAlmoxarifados] = useState<PesquisaAlmox[]>([]);
  const [marcas, setMarcas] = useState<PesquisaMarca[]>([]);
  const [grupos, setGrupos] = useState<PesquisaGrupo[]>([]);
  const [carregandoDados, setCarregandoDados] = useState(true);

  // Seleções
  const [empresasSel, setEmpresasSel] = useState<PesquisaEmpresa[]>([]);
  const [almoxSel, setAlmoxSel] = useState<PesquisaAlmox[]>([]);
  const [marcasSel, setMarcasSel] = useState<PesquisaMarca[]>([]);
  const [gruposSel, setGruposSel] = useState<PesquisaGrupo[]>([]);

  // Filtros
  const [agruparPor, setAgruparPor] = useState('NAO AGRUPAR');
  const [ordemSelecionada, setOrdemSelecionada] = useState('NOME_PRODUTO');
  const [dataUltEntInicial, setDataUltEntInicial] = useState('');
  const [dataUltEntFinal, setDataUltEntFinal] = useState('');
  const [dataCadInicial, setDataCadInicial] = useState('');
  const [dataCadFinal, setDataCadFinal] = useState('');
  const [apenasAtivo, setApenasAtivo] = useState(false);
  const [apenasDisponivel, setApenasDisponivel] = useState(false);
  const [apenasComEstoque, setApenasComEstoque] = useState(true);
  const [apenasEnviaMobile, setApenasEnviaMobile] = useState(true);

  // UI
  const [executando, setExecutando] = useState(false);
  const [arquivoParaDownload, setArquivoParaDownload] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const logsRef = useRef<HTMLDivElement>(null);

  // Histórico
  const [historico, setHistorico] = useState<CatalogoHistorico[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  // Toast/ConfirmDialog globais (migrado do controlled-state legacy).
  const toast = useToast();
  const confirm = useConfirmDialog();


  // SignalR
  const { connection } = useSignalR();

  // Carregar dados na montagem
  useEffect(() => {
    const load = async () => {
      setCarregandoDados(true);
      try {
        const [emp, alm, mar, grp] = await Promise.all([
          pesquisasService.getEmpresas(),
          pesquisasService.getAlmoxarifados(),
          pesquisasService.getMarcas(),
          pesquisasService.getGrupos(),
        ]);
        setEmpresas(emp);
        setAlmoxarifados(alm);
        setMarcas(mar);
        setGrupos(grp);
      } catch (e) {
        logError('Erro ao carregar dados:', e);
      } finally {
        setCarregandoDados(false);
      }
    };
    load();
  }, []);

  // SignalR: logs em tempo real
  useEffect(() => {
    if (!connection) return;
    const handler = (msg: string) => {
      setLogs(prev => [...prev, msg]);
    };
    connection.on('ReceberLog', handler);
    return () => { connection.off('ReceberLog', handler); };
  }, [connection]);

  // Auto-scroll logs
  useEffect(() => {
    logsRef.current?.scrollTo({ top: logsRef.current.scrollHeight, behavior: 'smooth' });
  }, [logs]);

  // Carregar histórico ao abrir a aba
  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    try {
      const data = await catalogoService.listarHistorico();
      setHistorico(data);
    } catch (e) {
      logError('Erro ao carregar histórico:', e);
      setHistorico([]);
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  // Carrega histórico na montagem e ao trocar de aba
  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  useEffect(() => {
    if (aba === 'historico') carregarHistorico();
  }, [aba, carregarHistorico]);


  const excluirCatalogo = useCallback(async (id: number) => {
    const ok = await confirm({
      mensagem: 'Excluir este catálogo?',
      destrutivo: true,
    });
    if (!ok) return;
    try {
      await catalogoService.excluirCatalogo(id);
      setHistorico(prev => prev.filter(c => c.id !== id));
      toast.sucesso('Catálogo excluído.');
    } catch {
      toast.erro('Erro ao excluir catálogo.');
    }
  }, [confirm, toast]);

  // Toggle helpers
  const toggleItem = <T,>(list: T[], item: T, getId: (i: T) => string): T[] => {
    const id = getId(item);
    return list.some(i => getId(i) === id)
      ? list.filter(i => getId(i) !== id)
      : [...list, item];
  };

  const gerarCatalogo = useCallback(async () => {
    setExecutando(true);
    setLogs([]);
    setArquivoParaDownload('');

    try {
      const filtros: FiltrosCatalogo = {
        empresasSelecionadas: empresasSel,
        almoxarifadosSelecionados: almoxSel,
        marcasSelecionadas: marcasSel,
        grupoSelecionados: gruposSel,
        agruparPor,
        ordemSelecionada,
        dataUltEntInicial: dataUltEntInicial ? new Date(dataUltEntInicial) : undefined,
        dataUltEntFinal: dataUltEntFinal ? new Date(dataUltEntFinal) : undefined,
        dataCadInicial: dataCadInicial ? new Date(dataCadInicial) : undefined,
        dataCadFinal: dataCadFinal ? new Date(dataCadFinal) : undefined,
        apenasAtivo,
        apenasDisponivel,
        apenasComEstoque,
        apenasEnviaMobile,
      };

      const nomeArquivo = await catalogoService.gerarCatalogo(filtros);
      setArquivoParaDownload(nomeArquivo);
      setLogs(prev => [...prev, 'Catálogo gerado com sucesso!']);
      // Recarregar histórico em background
      carregarHistorico().catch(() => {});
    } catch (error: unknown) {
      let msg = 'Erro desconhecido';
      if (error && typeof error === 'object' && 'response' in error) {
        const ax = error as { response?: { status?: number; data?: unknown }; message?: string };
        msg = typeof ax.response?.data === 'string' ? ax.response.data : ax.message ?? msg;
      }
      setLogs(prev => [...prev, `Erro: ${msg}`]);
    } finally {
      setExecutando(false);
    }
  }, [empresasSel, almoxSel, marcasSel, gruposSel, agruparPor, ordemSelecionada, dataUltEntInicial, dataUltEntFinal, dataCadInicial, dataCadFinal, apenasAtivo, apenasDisponivel, apenasComEstoque, apenasEnviaMobile]);

  const podeGerar = empresasSel.length > 0 && almoxSel.length > 0 && !executando;

  return (
    <div className="mx-auto max-w-[980px] space-y-4 md:space-y-5">
      {/* Header + Abas */}
      <div className="flex items-center gap-3 flex-wrap">
        <DocumentTextIcon className="w-7 h-7 text-accent-text flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Catálogo</h1>
          <p className="text-xs text-tx-2">Gere PDF de catálogo de produtos com filtros customizáveis</p>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setAba('gerar')}
            className={`px-3 py-1.5 text-xs font-medium rounded-iris transition-colors ${
              aba === 'gerar'
                ? 'bg-accent text-white'
                : 'text-tx-2 hover:bg-surface-2'
            }`}
          >
            <PlayIcon className="w-3.5 h-3.5 inline mr-1" />
            Gerar
          </button>
          <button
            onClick={() => setAba('historico')}
            className={`px-3 py-1.5 text-xs font-medium rounded-iris transition-colors ${
              aba === 'historico'
                ? 'bg-accent text-white'
                : 'text-tx-2 hover:bg-surface-2'
            }`}
          >
            <ClockIcon className="w-3.5 h-3.5 inline mr-1" />
            PDF
            {historico.length > 0 && aba !== 'historico' && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-surface-2 text-tx-2">
                {historico.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Aba: Histórico ── */}
      {aba === 'historico' && (
        <div className="bg-surface border border-line rounded-iris-lg shadow-sm overflow-hidden">
          {carregandoHistorico ? (
            <div className="p-8 text-center text-tx-3">
              <ArrowPathIcon className="w-5 h-5 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : historico.length === 0 ? (
            <div className="p-8 text-center text-tx-3 text-sm">
              Nenhum catálogo gerado ainda.
            </div>
          ) : (
            <div className="divide-y divide-line">
              {historico.map(cat => {
                const dt = new Date(cat.criadoEm);
                const data = dt.toLocaleDateString('pt-BR');
                const hora = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                const tamanho = cat.tamanhoBytes > 1024 * 1024
                  ? `${(cat.tamanhoBytes / (1024 * 1024)).toFixed(1)} MB`
                  : `${Math.round(cat.tamanhoBytes / 1024)} KB`;

                return (
                  <div key={cat.id} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors">
                    <DocumentTextIcon className="w-5 h-5 text-danger flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-tx truncate">
                        {cat.nomeArquivo}
                      </p>
                      <p className="text-xs text-tx-3">
                        {data} {hora} · {tamanho}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try { await catalogoService.baixarArquivo(cat.nomeArquivo); }
                        catch { toast.erro('Erro ao baixar.'); }
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-iris
                                 bg-accent hover:opacity-90 text-white transition-opacity"
                    >
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Baixar</span>
                    </button>
                    <button
                      onClick={() => excluirCatalogo(cat.id)}
                      className="p-1.5 text-tx-3 hover:text-danger transition-colors rounded"
                      title="Excluir"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="px-4 py-2 border-t border-line text-[11px] text-tx-3">
            Catálogos são removidos automaticamente após 15 dias.
          </div>
        </div>
      )}

      {/* ── Aba: Gerar ── */}
      {aba === 'gerar' && (<>


      {/* Seleções — inline dropdowns */}
      <div className="bg-surface border border-line rounded-iris-lg shadow-sm p-3 md:p-5">
        <h2 className="text-sm font-semibold text-tx-2 mb-3">Seleções</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DropdownMulti
            label="Empresas"
            icon={<BuildingOfficeIcon className="w-4 h-4" />}
            items={empresas}
            selected={empresasSel}
            onToggle={item => setEmpresasSel(prev => toggleItem(prev, item, i => i.codigo))}
            getId={i => i.codigo}
            getLabel={i => i.nomeFantasia || i.razaosocial}
            getSubLabel={i => i.razaosocial}
            loading={carregandoDados}
            placeholder="Buscar empresa..."
            required
          />
          <DropdownMulti
            label="Almoxarifados"
            icon={<BuildingStorefrontIcon className="w-4 h-4" />}
            items={almoxarifados}
            selected={almoxSel}
            onToggle={item => setAlmoxSel(prev => toggleItem(prev, item, i => i.codigoAlmox))}
            getId={i => i.codigoAlmox}
            getLabel={i => i.descicaoAlmox}
            loading={carregandoDados}
            placeholder="Buscar almoxarifado..."
            required
          />
          <DropdownMulti
            label="Marcas"
            icon={<TagIcon className="w-4 h-4" />}
            items={marcas}
            selected={marcasSel}
            onToggle={item => setMarcasSel(prev => toggleItem(prev, item, i => i.codigomarca))}
            getId={i => i.codigomarca}
            getLabel={i => i.descricaomarca}
            loading={carregandoDados}
            placeholder="Buscar marca..."
          />
          <DropdownMulti
            label="Grupos"
            icon={<FolderIcon className="w-4 h-4" />}
            items={grupos}
            selected={gruposSel}
            onToggle={item => setGruposSel(prev => toggleItem(prev, item, i => i.codigo))}
            getId={i => i.codigo}
            getLabel={i => i.descricao}
            loading={carregandoDados}
            placeholder="Buscar grupo..."
          />
        </div>
      </div>

      {/* Configurações */}
      <div className="bg-surface border border-line rounded-iris-lg shadow-sm p-4 md:p-5 space-y-5">
        <h2 className="text-sm font-semibold text-tx-2">Configurações</h2>

        {/* Critérios */}
        <div>
          <h3 className="text-xs font-semibold text-tx-3 uppercase tracking-wide mb-2">Critérios</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Agrupar por</label>
              <select value={agruparPor} onChange={e => setAgruparPor(e.target.value)}
                className={`w-full ${inputIris}`}>
                {OPCOES_AGRUPAMENTO.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Ordenar por</label>
              <select value={ordemSelecionada} onChange={e => setOrdemSelecionada(e.target.value)}
                className={`w-full ${inputIris}`}>
                {Object.entries(OPCOES_ORDENACAO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Datas */}
        <div>
          <h3 className="text-xs font-semibold text-tx-3 uppercase tracking-wide mb-2">Datas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Ult. Entrada: de</label>
              <input type="date" value={dataUltEntInicial} onChange={e => setDataUltEntInicial(e.target.value)}
                className={`w-full ${inputIris}`} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Ult. Entrada: até</label>
              <input type="date" value={dataUltEntFinal} onChange={e => setDataUltEntFinal(e.target.value)}
                className={`w-full ${inputIris}`} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Cadastro: de</label>
              <input type="date" value={dataCadInicial} onChange={e => setDataCadInicial(e.target.value)}
                className={`w-full ${inputIris}`} />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide font-medium text-tx-3 mb-1">Cadastro: até</label>
              <input type="date" value={dataCadFinal} onChange={e => setDataCadFinal(e.target.value)}
                className={`w-full ${inputIris}`} />
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div>
          <h3 className="text-xs font-semibold text-tx-3 uppercase tracking-wide mb-2">Filtros</h3>
          <FiltrosProduto
            value={{ apenasAtivo, apenasComEstoque, apenasDisponivelVenda: apenasDisponivel, apenasEnviaMobile }}
            onChange={patch => {
              if (patch.apenasAtivo !== undefined) setApenasAtivo(patch.apenasAtivo);
              if (patch.apenasComEstoque !== undefined) setApenasComEstoque(patch.apenasComEstoque);
              if (patch.apenasDisponivelVenda !== undefined) setApenasDisponivel(patch.apenasDisponivelVenda);
              if (patch.apenasEnviaMobile !== undefined) setApenasEnviaMobile(patch.apenasEnviaMobile);
            }}
          />
        </div>
      </div>

      {/* Ação principal */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={gerarCatalogo}
          disabled={!podeGerar}
          className={btnPrimario}
        >
          {executando ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
          {executando ? 'Gerando...' : 'Gerar Catálogo'}
        </button>

        {!podeGerar && !executando && (
          <span className="text-xs text-danger">Selecione empresa e almoxarifado</span>
        )}
      </div>

      {/* Status card — progresso, sucesso e logs unificados */}
      {(executando || logs.length > 0) && (
        <div className="bg-surface border border-line rounded-iris-lg shadow-sm overflow-hidden">
          {/* Header de status */}
          {executando ? (
            <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
              <ArrowPathIcon className="w-5 h-5 text-accent-text animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-tx">Gerando catálogo...</h2>
                <p className="text-xs text-tx-2">Isso pode levar alguns segundos</p>
              </div>
            </div>
          ) : arquivoParaDownload ? (
            <div className="flex items-center gap-3 px-4 py-3 bg-ok-soft border-b border-ok-border">
              <CheckCircleIcon className="w-5 h-5 text-ok flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-ok">Catálogo gerado!</h2>
                <p className="text-xs text-ok/80 truncate">{arquivoParaDownload}</p>
              </div>
              <button
                onClick={async () => {
                  try { await catalogoService.baixarArquivo(arquivoParaDownload); }
                  catch { toast.erro('Erro ao baixar arquivo.'); }
                }}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-iris bg-accent hover:opacity-90 text-white transition-opacity shadow-sm"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                Baixar PDF
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
              <DocumentTextIcon className="w-5 h-5 text-tx-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-tx-2">Última execução</h2>
              </div>
            </div>
          )}

          {/* Barra de progresso */}
          {executando && (
            <div className="h-1 bg-surface-2">
              <div className="h-full bg-accent rounded-full animate-pulse" style={{ width: '100%' }} />
            </div>
          )}

          {/* Logs */}
          <div ref={logsRef} className="p-3 max-h-48 overflow-y-auto font-mono text-xs text-tx-2 space-y-0.5">
            {logs.length === 0 && executando && (
              <div className="text-tx-3">Aguardando início do processo...</div>
            )}
            {logs.map((log, i) => (
              <div key={i} className={log.startsWith('Erro') ? 'text-danger' : ''}>{log}</div>
            ))}
          </div>
        </div>
      )}
      </>)}

    </div>
  );
};
